import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Select } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../../custom';
import Input from '../../../custom/input/input';
import { createAdminUser, getRoles } from '../request';
import { useState } from 'react';

interface Props {
  handleClose: () => void;
  refetch: () => void;
}

const AddAdminUser = ({ handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const validate = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    //roleName: Yup.array().min(1, 'At least one role is required').required('Role is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number',
      )
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const addUserMutation = useMutation({
    mutationFn: createAdminUser,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['get-roles'],
    queryFn: getRoles,
  });

  const roles = data as Role[];
  const roleOptions = roles?.map((role) => ({ label: role.name, value: role.name })) || [];

  const addUserHandler = async (values: FormikValues, resetForm: () => void) => {
    const payload: CreateAdminUserPayload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      roleName: selectedRoles.map((role) => ({ roleName: role })),
      password: values.password,
      confirmPassword: values.confirmPassword,
    };

    try {
      await addUserMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ['get-admin-users'] });
          refetch();

          resetForm();
          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message,
      });
    }
  };

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
      }}
      onSubmit={(values, { resetForm }) => {
        addUserHandler(values, resetForm);
      }}
      validationSchema={validate}
      enableReinitialize={true}
    >
      {() => {
        return (
          <Form className="fields">
            <Input label="First Name" placeholder="Input First Name" name="firstName" />

            <Input label="Last Name" placeholder="Input Last Name" name="lastName" />

            <Input label="Email Address" placeholder="Input Email Address" name="email" />

            <Input label="Phone Number" placeholder="Input Phone Number" name="phoneNumber" />

            <Input label="Password" placeholder="Input Password" name="password" type="password" />

            <Input label="Confirm Password" placeholder="Confirm Password" name="confirmPassword" type="password" />

            <>
              <h5>Roles</h5>
              <Select
                placeholder="Select Role"
                options={roleOptions}
                allowClear
                mode="multiple"
                value={selectedRoles}
                onChange={(value) => setSelectedRoles(value)}
                loading={isLoading}
                size="large"
              />
            </>

            <div className="btn-group">
              <Button onClick={handleClose} variant="text" text="Cancel" />
              <Button
                text={addUserMutation.isPending ? 'Submitting...' : 'Submit'}
                type="submit"
                isLoading={addUserMutation?.isPending}
                disabled={addUserMutation?.isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddAdminUser;
